import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Space, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_PORTAL_ORDER_EXPORT_TASK } from 'graphql/queries/orderExport';
import { PortalOrderExportTask } from 'graphql/types';
import { useOrderExportDownload } from 'hooks/useOrderExportDownload';
import { isTerminalStatus } from 'utils/orderExport';

/** 轮询间隔，与账单导出弹窗一致 */
export const POLL_INTERVAL_MS = 5000;

/**
 * 轮询次数上限。
 *
 * 第 1 次在弹窗打开时立即发出，其余 11 次每 5 秒一发，覆盖约 55 秒。
 * 到点仍未落终态就停下并提示「可稍后到导出记录查看」——导出是异步的，任务不会因为
 * 前端不再轮询而中断，继续轮下去只是白白占着连接。
 */
export const MAX_POLLS = 12;

type Props = {
    open: boolean;
    /** 刚提交成功的任务；为 null 时不轮询 */
    taskId: string | null;
    /**
     * @param stillRunning 关闭时任务是否仍未落终态。调用方据此决定要不要提示「可稍后到导出记录查看」——
     *                     任务已经完成（甚至已下载）还弹这句话，客户会以为文件没生成出来。
     */
    onClose: (stillRunning: boolean) => void;
};

/**
 * 提交成功后的等待弹窗：轮询单个任务，完成后由客户手动点「下载」。
 *
 * <p><b>刻意不自动触发下载</b>（产品 2026-07-30 确认）：自动 window.open 会被浏览器的
 * 弹窗拦截器挡下——它只放行由用户手势直接触发的打开，而轮询回调不算手势，
 * 客户会看到「已完成」却什么都没下下来。
 *
 * <p><b>刻意不显示百分比</b>：服务端不下发进度（生成阶段是流式写 Excel，没有可信的完成比例），
 * 前端自己编一个只会在 90% 处停很久，比转圈更让人焦虑。
 */
const OrderExportProgressModal: React.FC<Props> = ({ open, taskId, onClose }) => {
    const { t } = useTranslation();
    const [task, setTask] = useState<PortalOrderExportTask | null>(null);
    const [timedOut, setTimedOut] = useState(false);
    const { download, downloadingTaskId } = useOrderExportDownload();
    const [fetchTask] = useLazyQuery<{ portalOrderExportTask: PortalOrderExportTask | null }>(
        GET_PORTAL_ORDER_EXPORT_TASK,
        { fetchPolicy: 'no-cache' }
    );

    useEffect(() => {
        if (!open || !taskId) return undefined;

        setTask(null);
        setTimedOut(false);

        let polls = 0;
        let timer = 0;
        /*
         * 弹窗关闭（含「后台运行」）后异步返回的那一次响应必须丢弃：
         * 不丢的话 setTask 会在组件已卸载/已重置的状态上写回旧任务，
         * 下次打开弹窗会先闪一下上一个任务的内容。
         */
        let cancelled = false;

        const poll = async () => {
            polls += 1;
            const { data, error } = await fetchTask({ variables: { taskId } });
            if (cancelled) return;

            /*
             * 查询本身失败（网络断了 / 服务端 5xx）就停止轮询，不重试。
             *
             * errorLink 对每个失败响应都会弹一条 message.error，接着轮下去等于在 55 秒里
             * 给客户摞满 12 条一模一样的红条，而多轮 11 次并不能让一个已经不通的后端变通。
             * 停下来复用「仍在生成」这条提示：任务在服务端确实还在跑，不会因为前端不看了就中断，
             * 客户的下一步动作（去导出记录页取文件）与轮询超时那条路径完全一致。
             */
            if (error) {
                window.clearInterval(timer);
                setTimedOut(true);
                return;
            }

            /* 查不到（跨渠道 / 已删）时保留上一次的展示，不把界面刷成空白 */
            const next = data?.portalOrderExportTask ?? null;
            if (next) setTask(next);

            if (next && isTerminalStatus(next.status)) {
                window.clearInterval(timer);
                return;
            }
            if (polls >= MAX_POLLS) {
                window.clearInterval(timer);
                setTimedOut(true);
            }
        };

        poll();
        timer = window.setInterval(poll, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [open, taskId, fetchTask]);

    const status = task?.status;
    const succeeded = status === 'SUCCESS';
    const failed = status === 'FAILED';
    /* 未落终态就是「还在生成」，包含任务尚未查回来的那一小段 */
    const running = !succeeded && !failed;

    return (
        <Modal
            open={open}
            title={t('orderExport.modal.title')}
            onCancel={() => onClose(running)}
            maskClosable={false}
            footer={
                <Button onClick={() => onClose(running)}>
                    {running ? t('orderExport.action.background') : t('orderExport.action.close')}
                </Button>
            }
            width={520}
        >
            <div className="space-y-4">
                {running && !timedOut && (
                    <Space>
                        <Spin />
                        <span>{t('orderExport.generatingHint')}</span>
                    </Space>
                )}

                {running && timedOut && (
                    <Alert type="info" showIcon message={t('orderExport.stillRunning')} />
                )}

                {failed && <Alert type="error" showIcon message={t('orderExport.failedHint')} />}

                {succeeded && (
                    <div className="space-y-2">
                        <Alert
                            type="success"
                            showIcon
                            message={t('orderExport.completedHint', { count: task?.totalCount ?? 0 })}
                        />
                        <div className="break-all text-sm text-gray-600">{task?.fileName || '-'}</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadingTaskId === task?.taskId}
                            onClick={() => task && download(task.taskId)}
                        >
                            {t('orderExport.action.download')}
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default OrderExportProgressModal;
